import React, { useCallback, useEffect, useState } from 'react'
import { Clinic } from '@customTypes/Clinic'
import { InitialStep } from './components/InitialStep/InitialStep'
import { VerificationStep } from './components/VerificationStep/VerificationStep'
import { SuccessStep } from './components/SuccessStep/SuccessStep'
import { useMutation } from 'react-query'
import { cancelAppointment } from 'src/services/api/cancelAppointment'
import { useTranslation } from 'react-i18next'
import { getCancellationErrorContentfulEntry } from 'src/utils/getOnlineCancellationErrorMessage'
import { cancellationPossibleWithWarning, getDiffInHours, isTooLateToCancel } from './helpers/verifyTime'
import { TooLateToCancel } from './components/TooLateToCancel/TooLateToCancel'
import { CancellationNotPossible } from './components/CancellationNotPossible/CancellationNotPossible'

type OnlineCancellationScreenProps = {
  appointmentBundleReferenceId: string
  clinic: Clinic | undefined
  date: Date
  isTreatmentCancellable: undefined | boolean
  loading: boolean
}

export enum CancelAppointmentStep {
  INITIAL = 'INITIAL',
  VERIFICATION = 'VERIFICATION',
  SUCCESS = 'SUCCESS',
  TOO_LATE_TO_CANCEL = 'TOO_LATE_TO_CANCEL',
  CANCELLATION_NOT_POSSIBLE = 'CANCELLATION_NOT_POSSIBLE',
}

export const OnlineCancellationScreen = ({
  appointmentBundleReferenceId,
  clinic,
  date,
  isTreatmentCancellable,
  loading,
}: OnlineCancellationScreenProps) => {
  const [currentStep, setCurrentStep] = useState<CancelAppointmentStep>(CancelAppointmentStep.INITIAL)
  const [name, setName] = useState<string>('')
  const [error, setError] = useState<string>('')
  const [warningCheckboxChecked, setWarningCheckboxChecked] = useState(false)
  const { t } = useTranslation()
  const diffInHours = getDiffInHours(date)
  const displayCancellationWarning = cancellationPossibleWithWarning(diffInHours)

  useEffect(() => {
    if (isTooLateToCancel(diffInHours)) {
      setCurrentStep(CancelAppointmentStep.TOO_LATE_TO_CANCEL)
    } else if (isTreatmentCancellable === false) {
      setCurrentStep(CancelAppointmentStep.CANCELLATION_NOT_POSSIBLE)
    }
  }, [diffInHours, isTreatmentCancellable])

  const { mutate: onCancelAppointment } = useMutation(cancelAppointment, {
    onSuccess: () => {
      setCurrentStep(CancelAppointmentStep.SUCCESS)
    },
    onError: (err: Record<string, any>) => {
      setError(t(getCancellationErrorContentfulEntry(err.response.data.statusCode)))
    },
  })

  const processCancellation = useCallback(() => {
    onCancelAppointment({
      appointmentBundleReferenceId,
      authString: name,
      acceptCancellationFee: warningCheckboxChecked,
    })
  }, [name, appointmentBundleReferenceId, warningCheckboxChecked, onCancelAppointment])

  const onVerificationStepConfirm = useCallback(() => {
    const diffInHours = getDiffInHours(date)
    if (isTooLateToCancel(diffInHours)) {
      setCurrentStep(CancelAppointmentStep.TOO_LATE_TO_CANCEL)
    } else if (cancellationPossibleWithWarning(diffInHours) && !warningCheckboxChecked) {
      setCurrentStep(CancelAppointmentStep.INITIAL)
    } else {
      processCancellation()
    }
  }, [processCancellation, date, warningCheckboxChecked])

  const onNameEntered = (value: string) => {
    setName(value)
  }

  const getStepComponent = (currentStep: CancelAppointmentStep) => {
    switch (currentStep) {
      case CancelAppointmentStep.INITIAL:
        return (
          <InitialStep
            date={date}
            clinic={clinic}
            onConfirm={() => setCurrentStep(CancelAppointmentStep.VERIFICATION)}
            displayCancellationWarning={displayCancellationWarning}
            warningCheckboxChecked={warningCheckboxChecked}
            toggleWarningCheckboxChecked={() => setWarningCheckboxChecked(!warningCheckboxChecked)}
            loading={loading}
          />
        )
      case CancelAppointmentStep.VERIFICATION:
        return (
          <VerificationStep
            onConfirm={onVerificationStepConfirm}
            onNameEntered={onNameEntered}
            onBackClick={() => setCurrentStep(CancelAppointmentStep.INITIAL)}
            buttonIsDisabled={name.length < 3}
            error={error}
          />
        )
      case CancelAppointmentStep.SUCCESS:
        return <SuccessStep date={date} />
      case CancelAppointmentStep.TOO_LATE_TO_CANCEL:
        return <TooLateToCancel phone={clinic?.phone} />
      case CancelAppointmentStep.CANCELLATION_NOT_POSSIBLE:
        return <CancellationNotPossible phone={clinic?.phone} />
      default:
        throw new Error(`Not a valid step.`)
    }
  }

  return getStepComponent(currentStep)
}
